import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable } from '@angular/core';

import { SnackType, TOAST_MESSAGE } from '../../utils/constants';

import { ToastContainerComponent, ToastData } from './toast-container.component';

const MAX_VISIBLE = 5;

@Injectable({
  providedIn: 'root',
})
export class CustomSnackbarService {
  private overlayRef?: OverlayRef;
  private containerRef?: ComponentRef<ToastContainerComponent>;
  private readonly visible: ToastData[] = [];
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private seq = 0;

  constructor(private overlay: Overlay) {}

  public openSnackBar(message: string, snackType?: SnackType, duration?: number) {
    this.ensureContainer();

    if (this.visible.length >= MAX_VISIBLE) {
      this.dismiss(this.visible[0].id);
    }

    const toast: ToastData = {
      id: ++this.seq,
      message,
      snackType: snackType !== undefined ? snackType : 'Success',
      duration: duration || TOAST_MESSAGE,
    };
    this.visible.push(toast);
    this.timers.set(
      toast.id,
      setTimeout(() => this.dismiss(toast.id), toast.duration)
    );
  }

  public closeSnackBar() {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.visible.length = 0;
    this.detach();
  }

  private dismiss(id: number): void {
    const index = this.visible.findIndex((toast) => toast.id === id);
    if (index > -1) {
      this.visible.splice(index, 1);
    }

    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    if (this.visible.length === 0) {
      this.detach();
    }
  }

  private ensureContainer(): void {
    if (this.containerRef) {
      return;
    }

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().bottom().centerHorizontally(),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
    this.containerRef = this.overlayRef.attach(new ComponentPortal(ToastContainerComponent));
    this.containerRef.instance.toasts = this.visible;
    this.containerRef.instance.dismiss = (id: number) => this.dismiss(id);
  }

  private detach(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    this.containerRef = undefined;
  }
}
